import { type ReactElement, type ReactNode, useState } from 'react';
import type {
  ControlledTableProps,
  FetcherState,
  RowType,
  TableController,
  TablePageProps,
  TableUrlSync,
} from './types';
import { useQueryState } from './useQueryState';
import { TablePage } from './TablePage';
import { TableFilter } from './TableFilter';
import { ColumnConfigPopover } from './ColumnConfigPopover';
import { useColumnConfig } from './useColumnConfig';
import { Navigate } from 'react-router';
import { Box, DataTable, Pagination } from '@ff-it/ui';
import cx from 'clsx';
import { useFetcher } from './useFetcher';
import { useColumnLayout } from './useColumnLayout';
import { useOverflowX } from './useOverflowX';
import * as styles from './ControlledTable.css';

const defaultPageSizes = [15, 30, 60];

export function ControlledTable<R extends RowType>(props: ControlledTableProps<R>): ReactElement {
  const {
    pageSize: constantPageSize,
    defaultPageSize,
    defaultFilter: outerDefaultFilter,
    storageKey,
    filterFields,
    actions,
    urlSync = 'push',
    queryParams,
    before,
    columns,
    tableProps: { after, containerClassName, tableContainerClassName, ...tableProps } = {},
    ...rest
  } = props;

  const { initialState, navigateTo, onStateChange, defaultFilter } = useFetcher({
    pageSize: constantPageSize,
    defaultPageSize,
    queryParams,
    defaultFilter: outerDefaultFilter,
    storageKey,
    urlSync,
  });

  const { configuredColumns, configurable, hasLeftPin, hasRightPin, columnActions } = useColumnConfig(
    storageKey ? `${storageKey}::cols` : undefined,
    columns,
  );
  const [tableContainer, setTableContainer] = useState<HTMLElement | null>(null);
  const tableColumns = useColumnLayout(configuredColumns, tableContainer);
  const hasOverflow = useOverflowX(tableContainer);

  const toolbar =
    filterFields !== null || actions || configurable ? (
      <>
        {filterFields !== null && <TableFilter>{filterFields}</TableFilter>}
        {(actions || configurable) && (
          <Box ml="auto" display="flex" alignItems="center" gap="3">
            {configurable && <ColumnConfigPopover configuredColumns={configuredColumns} {...columnActions} />}
            {actions}
          </Box>
        )}
      </>
    ) : undefined;

  if (navigateTo) {
    return <Navigate to={navigateTo} replace={true} />;
  }

  function renderTable({
    data,
    onPageChange,
    pageIndex,
    pageSize,
    columns,
    sort,
    onSort,
    isLoading,
  }: TableController<R>): ReactNode {
    const pagination = Number.isFinite(pageSize) ? (
      <Pagination
        current={pageIndex}
        pageSize={pageSize}
        total={data?.total || 0}
        // no pageSizeOptions if page size is constant
        pageSizeOptions={constantPageSize ? undefined : defaultPageSizes}
        onChange={onPageChange}
        size={tableProps?.size as any}
      />
    ) : null;

    return (
      <>
        {before}
        <DataTable<R>
          sort={sort}
          onSort={onSort}
          data={data ? data.rows : []}
          columns={columns}
          loading={isLoading}
          tableContainerRef={setTableContainer}
          containerClassName={cx(styles.card, containerClassName)}
          tableContainerClassName={cx(
            styles.tableScroll,
            hasLeftPin && styles.pinnedLeft,
            hasRightPin && styles.pinnedRight,
            hasOverflow.start && styles.overflowStart,
            hasOverflow.end && styles.overflowEnd,
            tableContainerClassName,
          )}
          before={toolbar ? <Box className={styles.toolbar}>{toolbar}</Box> : undefined}
          after={
            pagination || after ? (
              <Box className={styles.footer}>
                <Box className={styles.footerPagination}>{pagination}</Box>
                {after}
              </Box>
            ) : undefined
          }
          {...tableProps}
        />
      </>
    );
  }

  return (
    <QueryTablePage
      initialState={initialState}
      onStateChange={onStateChange}
      defaultFilter={defaultFilter}
      renderTable={renderTable}
      urlSync={urlSync}
      columns={tableColumns}
      {...rest}
    />
  );
}

const QueryTablePage = ({
  initialState,
  onStateChange,
  urlSync,
  ...props
}: Omit<TablePageProps<any>, 'state' | 'setState'> & {
  initialState: FetcherState;
  onStateChange?: (state: FetcherState) => void;
  urlSync?: TableUrlSync;
}): ReactElement => {
  const [state, setState] = useQueryState(initialState, onStateChange, urlSync);
  return <TablePage state={state} setState={setState} {...props} />;
};
